// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2025-2026 Daragh Farrell

import { lazy, Suspense } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import Layout from '../components/Layout';
import ErrorBoundary from '../components/ErrorBoundary';
import { useAuth } from '../auth/AuthProvider';
import { useDeepLinks } from '../hooks/useDeepLinks';
import LoadingSpinner from '../components/LoadingSpinner';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { VariantRoute } from '../components/VariantRoute';

// Eagerly loaded: auth pages (critical path, small)
import Login from '../pages/Login';
import Register from '../pages/Register';
import RegisterClub from '../pages/RegisterClub';
import RegistrationPending from '../pages/RegistrationPending';
import ClubPendingApproval from '../pages/ClubPendingApproval';
import RequestPasswordReset from '../pages/RequestPasswordReset';
import ResetPassword from '../pages/ResetPassword';

// Lazy loaded: all authenticated pages (code-split per route)
const Dashboard = lazy(() => import('../pages/Dashboard'));
const Members = lazy(() => import('../pages/Members'));
const CreateMember = lazy(() => import('../pages/CreateMember'));
const EditMember = lazy(() => import('../pages/EditMember'));
const MemberDetail = lazy(() => import('../pages/MemberDetail'));
const PendingRegistrations = lazy(() => import('../pages/PendingRegistrations'));
const MembershipPlans = lazy(() => import('../pages/MembershipPlans'));
const CreateMembershipPlan = lazy(() => import('../pages/CreateMembershipPlan'));
const EditMembershipPlan = lazy(() => import('../pages/EditMembershipPlan'));
const Events = lazy(() => import('../pages/Events'));
const CreateEvent = lazy(() => import('../pages/CreateEvent'));
const EditEvent = lazy(() => import('../pages/EditEvent'));
const MemberEvents = lazy(() => import('../pages/MemberEvents'));
const MyEventRegistrations = lazy(() => import('../pages/MyEventRegistrations'));
const MembershipSubscriptions = lazy(() => import('../pages/MembershipSubscriptions'));
const CreateSubscription = lazy(() => import('../pages/CreateSubscription'));
const Payments = lazy(() => import('../pages/Payments'));
const CreatePayment = lazy(() => import('../pages/CreatePayment'));
const Rooms = lazy(() => import('../pages/Rooms'));
const CreateRoom = lazy(() => import('../pages/CreateRoom'));
const EditRoom = lazy(() => import('../pages/EditRoom'));
const RoomCalendar = lazy(() => import('../pages/RoomCalendar'));
const PublicDirectory = lazy(() => import('../pages/PublicDirectory'));
const StatusPage = lazy(() => import('../pages/StatusPage'));
const ClubSettings = lazy(() => import('../pages/ClubSettings'));
const PaymentSettings = lazy(() => import('../pages/PaymentSettings'));
const ScheduleConflicts = lazy(() => import('../pages/ScheduleConflicts'));
const ScheduleSuggestions = lazy(() => import('../pages/ScheduleSuggestions'));
const Reservations = lazy(() => import('../pages/Reservations'));
const CreateReservation = lazy(() => import('../pages/CreateReservation'));
const ReservationDetail = lazy(() => import('../pages/ReservationDetail'));
const FacilityBrowser = lazy(() => import('../pages/FacilityBrowser'));
const FacilityDetail = lazy(() => import('../pages/FacilityDetail'));
const BookRoom = lazy(() => import('../pages/BookRoom'));
const Billing = lazy(() => import('../pages/Billing'));
const BillingDashboard = lazy(() => import('../pages/BillingDashboard'));
const MembershipCheckout = lazy(() => import('../pages/MembershipCheckout'));
const EventCheckout = lazy(() => import('../pages/EventCheckout'));
const BookingCheckout = lazy(() => import('../pages/BookingCheckout'));
const CheckoutSuccess = lazy(() => import('../pages/CheckoutSuccess'));
const CheckoutFailure = lazy(() => import('../pages/CheckoutFailure'));
const AdminLogin = lazy(() => import('../pages/AdminLogin'));
const RevenueDashboard = lazy(() => import('../pages/RevenueDashboard'));
const VersionDashboard = lazy(() => import('../pages/VersionDashboard'));
const PortalHome = lazy(() => import('../pages/PortalHome'));
const PortalProfile = lazy(() => import('../pages/PortalProfile'));
const AccountProfile = lazy(() => import('../pages/AccountProfile'));
const PortalPayments = lazy(() => import('../pages/PortalPayments'));
const PortalSubscriptions = lazy(() => import('../pages/PortalSubscriptions'));
const PortalEvents = lazy(() => import('../pages/PortalEvents'));
const NotificationPreferences = lazy(() => import('../pages/NotificationPreferences'));
const PortalTimetable = lazy(() => import('../pages/PortalTimetable'));
const SeriesRegistration = lazy(() => import('../pages/SeriesRegistration'));
const OnboardingWizard = lazy(() => import('../pages/OnboardingWizard'));
const ImportMembers = lazy(() => import('../pages/ImportMembers'));
const RefundList = lazy(() => import('../pages/RefundList'));
const RefundDetail = lazy(() => import('../pages/RefundDetail'));
const BulkEmail = lazy(() => import('../pages/BulkEmail'));
const JoinClub = lazy(() => import('../pages/JoinClub'));
const Terms = lazy(() => import('../pages/Terms'));
const Privacy = lazy(() => import('../pages/Privacy'));
const Cookies = lazy(() => import('../pages/Cookies'));
const DPA = lazy(() => import('../pages/DPA'));
const AccountSecurity = lazy(() => import('../pages/AccountSecurity'));
const ClubLandingPage = lazy(() => import('../pages/ClubLandingPage'));
const BrandingSettings = lazy(() => import('../pages/BrandingSettings'));
const LandingPageSettings = lazy(() => import('../pages/LandingPageSettings'));
const ReferralDashboard = lazy(() => import('../pages/ReferralDashboard'));
const HouseholdManager = lazy(() => import('../pages/HouseholdManager'));
const HouseholdDiscountRules = lazy(() => import('../pages/HouseholdDiscountRules'));
const ReconciliationDashboard = lazy(() => import('../pages/ReconciliationDashboard'));
const EmailTemplates = lazy(() => import('../pages/EmailTemplates'));
const TerminologySettings = lazy(() => import('../pages/TerminologySettings'));
const TermList = lazy(() => import('../pages/TermList'));
const CreateTerm = lazy(() => import('../pages/CreateTerm'));
const EditTerm = lazy(() => import('../pages/EditTerm'));
const TermDetail = lazy(() => import('../pages/TermDetail'));
const TimetableSlots = lazy(() => import('../pages/TimetableSlots'));
const TermCalendar = lazy(() => import('../pages/TermCalendar'));
const InstructorAvailability = lazy(() => import('../pages/InstructorAvailability'));
const CommitteeSettings = lazy(() => import('../pages/CommitteeSettings'));
const GallerySettings = lazy(() => import('../pages/GallerySettings'));
const DataExport = lazy(() => import('../pages/DataExport'));
const ProductList = lazy(() => import('../pages/ProductList'));
const CreateProduct = lazy(() => import('../pages/CreateProduct'));
const EditProduct = lazy(() => import('../pages/EditProduct'));
const ShopBrowser = lazy(() => import('../pages/ShopBrowser'));
const OrderList = lazy(() => import('../pages/OrderList'));
const MyOrders = lazy(() => import('../pages/MyOrders'));
const ShopCart = lazy(() => import('../pages/ShopCart'));
const CommissionDashboard = lazy(() => import('../pages/CommissionDashboard'));
const ClubCommission = lazy(() => import('../pages/ClubCommission'));
const PayoutReports = lazy(() => import('../pages/PayoutReports'));
const ClubPayouts = lazy(() => import('../pages/ClubPayouts'));
const DonationPage = lazy(() => import('../pages/DonationPage'));
const DonationList = lazy(() => import('../pages/DonationList'));
const FundraisingGoalSettings = lazy(() => import('../pages/FundraisingGoalSettings'));
const SponsorList = lazy(() => import('../pages/SponsorList'));
const CreateSponsor = lazy(() => import('../pages/CreateSponsor'));
const EditSponsor = lazy(() => import('../pages/EditSponsor'));
const TransferTicket = lazy(() => import('../pages/TransferTicket'));
const TransferResponse = lazy(() => import('../pages/TransferResponse'));
const MyTransfers = lazy(() => import('../pages/MyTransfers'));
const EventTransfers = lazy(() => import('../pages/EventTransfers'));
const VoucherPurchase = lazy(() => import('../pages/VoucherPurchase'));
const VoucherList = lazy(() => import('../pages/VoucherList'));
const PortalCredit = lazy(() => import('../pages/PortalCredit'));
const PlatformClubs = lazy(() => import('../pages/PlatformClubs'));
const NoticeBoard = lazy(() => import('../pages/NoticeBoard'));
const NoticeComposer = lazy(() => import('../pages/NoticeComposer'));
const NoticeManage = lazy(() => import('../pages/NoticeManage'));
const Equipment = lazy(() => import('../pages/Equipment'));
const Analytics = lazy(() => import('../pages/Analytics'));
const ApiTokens = lazy(() => import('../pages/ApiTokens'));
const ActiveSessions = lazy(() => import('../pages/ActiveSessions'));
import AdminLayout from '../components/AdminLayout';
import PortalLayout from '../components/PortalLayout';
import MarketingLayout from '../components/MarketingLayout';

// Lazy loaded: marketing pages (public, code-split)
const HomePage = lazy(() => import('../pages/marketing/HomePage'));
const PricingPage = lazy(() => import('../pages/marketing/PricingPage'));
const WhyClubViewPage = lazy(() => import('../pages/marketing/WhyClubViewPage'));

function SuspenseFallback() {
  return (
    <div className="flex h-64 items-center justify-center">
      <LoadingSpinner />
    </div>
  );
}

function RequireAuth({ children }: { children: JSX.Element }) {
  const { isAuthenticating, isAuthenticated } = useAuth();

  // Wait for auth state to be determined before redirecting
  if (isAuthenticating) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  // Only redirect after we've confirmed auth state
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function LazyPage({ children }: { children: JSX.Element }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<SuspenseFallback />}>{children}</Suspense>
    </ErrorBoundary>
  );
}

function MarketingPage({ children }: { children: JSX.Element }) {
  return (
    <MarketingLayout>
      <LazyPage>{children}</LazyPage>
    </MarketingLayout>
  );
}

/** Show marketing homepage for unauthenticated users, redirect to dashboard for authenticated */
function SmartHome() {
  const { isAuthenticating, isAuthenticated } = useAuth();

  if (isAuthenticating) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <MarketingPage>
      <HomePage />
    </MarketingPage>
  );
}

function AuthLayout({ children }: { children: JSX.Element }) {
  return (
    <RequireAuth>
      <Layout>
        <LazyPage>{children}</LazyPage>
      </Layout>
    </RequireAuth>
  );
}

function PortalAuthLayout({ children }: { children: JSX.Element }) {
  return (
    <RequireAuth>
      <PortalLayout>
        <LazyPage>{children}</LazyPage>
      </PortalLayout>
    </RequireAuth>
  );
}

export default function AppRoutes() {
  useDeepLinks();

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<RequestPasswordReset />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/register" element={<Register />} />
      <Route path="/register-club" element={<RegisterClub />} />
      <Route path="/registration-pending" element={<RegistrationPending />} />
      <Route path="/club-pending-approval" element={<ClubPendingApproval />} />
      <Route path="/directory" element={<LazyPage><PublicDirectory /></LazyPage>} />
      <Route path="/status" element={<LazyPage><StatusPage /></LazyPage>} />
      <Route path="/terms" element={<LazyPage><Terms /></LazyPage>} />
      <Route path="/privacy" element={<LazyPage><Privacy /></LazyPage>} />
      <Route path="/cookies" element={<LazyPage><Cookies /></LazyPage>} />
      <Route path="/dpa" element={<LazyPage><DPA /></LazyPage>} />
      <Route path="/clubs/:slug" element={<LazyPage><ClubLandingPage /></LazyPage>} />
      {/* Marketing pages (public) */}
      <Route path="/" element={<SmartHome />} />
      <Route path="/pricing" element={<MarketingPage><PricingPage /></MarketingPage>} />
      <Route path="/why-clubview" element={<MarketingPage><WhyClubViewPage /></MarketingPage>} />
      <Route
        path="/dashboard"
        element={
          <AuthLayout>
            <ProtectedRoute requiredRoles={['admin', 'treasurer', 'coach', 'instructor', 'organiser', 'dso']} redirectTo="/portal">
              <Dashboard />
            </ProtectedRoute>
          </AuthLayout>
        }
      />
      <Route
        path="/payments"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin', 'treasurer']}>
                <LazyPage><Payments /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/payments/new"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin', 'treasurer']}>
                <LazyPage><CreatePayment /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/refunds"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><RefundList /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/refunds/:id"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><RefundDetail /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/membership-plans"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><MembershipPlans /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/membership-plans/new"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><CreateMembershipPlan /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/membership-plans/:id/edit"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><EditMembershipPlan /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/members"
        element={
          <AuthLayout>
            <ProtectedRoute requiredRoles={['admin', 'treasurer', 'coach', 'instructor', 'organiser', 'dso']} redirectTo="/portal">
              <Members />
            </ProtectedRoute>
          </AuthLayout>
        }
      />
      <Route
        path="/members/new"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><CreateMember /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/members/:id/edit"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']} allowSelf>
                <LazyPage><EditMember /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/members/:id"
        element={<AuthLayout><MemberDetail /></AuthLayout>}
      />
      <Route
        path="/members/pending-registrations"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin', 'coach']}>
                <LazyPage><PendingRegistrations /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route path="/events" element={<AuthLayout><Events /></AuthLayout>} />
      <Route
        path="/events/new"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin', 'coach']}>
                <LazyPage><CreateEvent /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/events/:id/edit"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin', 'coach']}>
                <LazyPage><EditEvent /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route path="/events/register" element={<AuthLayout><MemberEvents /></AuthLayout>} />
      <Route path="/events/my-registrations" element={<AuthLayout><MyEventRegistrations /></AuthLayout>} />
      <Route
        path="/membership-subscriptions"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><MembershipSubscriptions /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/membership-subscriptions/new"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><CreateSubscription /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route path="/rooms" element={<AuthLayout><Rooms /></AuthLayout>} />
      <Route
        path="/rooms/new"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><CreateRoom /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/rooms/:id/edit"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><EditRoom /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/rooms/calendar"
        element={
          <RequireAuth>
            <Layout>
              <LazyPage><RoomCalendar /></LazyPage>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/reservations"
        element={
          <RequireAuth>
            <Layout>
              <LazyPage><Reservations /></LazyPage>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/reservations/new"
        element={
          <RequireAuth>
            <Layout>
              <LazyPage><CreateReservation /></LazyPage>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/reservations/:id"
        element={
          <RequireAuth>
            <Layout>
              <LazyPage><ReservationDetail /></LazyPage>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/facilities"
        element={
          <RequireAuth>
            <Layout>
              <VariantRoute required="v2">
                <LazyPage><FacilityBrowser /></LazyPage>
              </VariantRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/facilities/:facilityId"
        element={
          <RequireAuth>
            <Layout>
              <VariantRoute required="v2">
                <LazyPage><FacilityDetail /></LazyPage>
              </VariantRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/facilities/:facilityId/rooms/:roomId/book"
        element={
          <RequireAuth>
            <Layout>
              <VariantRoute required="v2">
                <LazyPage><BookRoom /></LazyPage>
              </VariantRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/billing"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><Billing /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/billing-dashboard"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><BillingDashboard /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/settings"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><ClubSettings /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/settings/payments"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><PaymentSettings /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/settings/notifications"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><NotificationPreferences /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/onboarding"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><OnboardingWizard /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/members/import"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><ImportMembers /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/email/compose"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><BulkEmail /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/settings/branding"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><BrandingSettings /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/settings/terminology"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><TerminologySettings /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/settings/landing-page"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><LandingPageSettings /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/settings/committee"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><CommitteeSettings /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/settings/gallery"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><GallerySettings /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      {/* C71: Data Export */}
      <Route
        path="/export"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><DataExport /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/referrals"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><ReferralDashboard /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/households"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><HouseholdManager /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/household-discounts"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><HouseholdDiscountRules /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/reconciliation"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><ReconciliationDashboard /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/email/templates"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><EmailTemplates /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      {/* Timetable routes */}
      <Route path="/timetable/terms" element={<AuthLayout><TermList /></AuthLayout>} />
      <Route path="/timetable/terms/new" element={<AuthLayout><CreateTerm /></AuthLayout>} />
      <Route path="/timetable/terms/:termId/edit" element={<AuthLayout><EditTerm /></AuthLayout>} />
      <Route path="/timetable/terms/:termId" element={<AuthLayout><TermDetail /></AuthLayout>} />
      <Route path="/timetable/terms/:termId/slots" element={<AuthLayout><TimetableSlots /></AuthLayout>} />
      <Route path="/timetable/calendar" element={<AuthLayout><TermCalendar /></AuthLayout>} />
      <Route
        path="/instructors/:memberId/availability"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin', 'coach']}>
                <LazyPage><InstructorAvailability /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route path="/schedule/conflicts" element={<AuthLayout><ScheduleConflicts /></AuthLayout>} />
      <Route path="/schedule/suggestions" element={<AuthLayout><ScheduleSuggestions /></AuthLayout>} />
      <Route path="/checkout/membership/:planId" element={<AuthLayout><MembershipCheckout /></AuthLayout>} />
      <Route path="/checkout/event/:eventId" element={<AuthLayout><EventCheckout /></AuthLayout>} />
      <Route path="/checkout/booking/:reservationId" element={<AuthLayout><BookingCheckout /></AuthLayout>} />
      <Route path="/checkout/success" element={<AuthLayout><CheckoutSuccess /></AuthLayout>} />
      <Route path="/checkout/failure" element={<AuthLayout><CheckoutFailure /></AuthLayout>} />
      {/* Portal routes (member self-service) */}
      <Route path="/portal" element={<PortalAuthLayout><PortalHome /></PortalAuthLayout>} />
      <Route path="/portal/profile" element={<PortalAuthLayout><PortalProfile /></PortalAuthLayout>} />
      <Route path="/portal/payments" element={<PortalAuthLayout><PortalPayments /></PortalAuthLayout>} />
      <Route path="/portal/subscriptions" element={<PortalAuthLayout><PortalSubscriptions /></PortalAuthLayout>} />
      <Route path="/portal/events" element={<PortalAuthLayout><PortalEvents /></PortalAuthLayout>} />
      <Route path="/portal/timetable" element={<PortalAuthLayout><PortalTimetable /></PortalAuthLayout>} />
      <Route path="/portal/series" element={<PortalAuthLayout><SeriesRegistration /></PortalAuthLayout>} />
      <Route path="/account" element={<AuthLayout><LazyPage><AccountProfile /></LazyPage></AuthLayout>} />
      <Route path="/account/security" element={<AuthLayout><AccountSecurity /></AuthLayout>} />
      {/* Shop routes */}
      <Route
        path="/products"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><ProductList /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/products/new"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><CreateProduct /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/products/:id/edit"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><EditProduct /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route path="/shop" element={<AuthLayout><ShopBrowser /></AuthLayout>} />
      <Route path="/my-orders" element={<AuthLayout><MyOrders /></AuthLayout>} />
      <Route path="/cart" element={<AuthLayout><ShopCart /></AuthLayout>} />
      <Route
        path="/orders"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><OrderList /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      {/* Commission & Payout routes */}
      <Route
        path="/club-commission"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><ClubCommission /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/club-payouts"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><ClubPayouts /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      {/* Donation routes */}
      <Route path="/clubs/:slug/donate" element={<LazyPage><DonationPage /></LazyPage>} />
      <Route
        path="/donations"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><DonationList /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/settings/fundraising"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><FundraisingGoalSettings /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      {/* Sponsor routes */}
      <Route
        path="/sponsors"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><SponsorList /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/sponsors/new"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><CreateSponsor /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/sponsors/:id/edit"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><EditSponsor /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      {/* Transfer routes */}
      <Route path="/events/:id/transfer" element={<AuthLayout><TransferTicket /></AuthLayout>} />
      <Route path="/transfers/:id" element={<AuthLayout><TransferResponse /></AuthLayout>} />
      <Route path="/portal/transfers" element={<PortalAuthLayout><MyTransfers /></PortalAuthLayout>} />
      <Route
        path="/events/:id/transfers"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><EventTransfers /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      {/* Voucher routes */}
      <Route path="/vouchers/purchase" element={<AuthLayout><VoucherPurchase /></AuthLayout>} />
      <Route
        path="/vouchers"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><VoucherList /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route path="/portal/credit" element={<PortalAuthLayout><PortalCredit /></PortalAuthLayout>} />
      {/* Notice Board routes */}
      <Route path="/notices" element={<AuthLayout><NoticeBoard /></AuthLayout>} />
      <Route
        path="/notices/new"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><NoticeComposer /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/notices/manage"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><NoticeManage /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/notices/:id/edit"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><NoticeComposer /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      {/* C73: Equipment, Analytics, API Tokens, Active Sessions */}
      <Route path="/equipment" element={<AuthLayout><Equipment /></AuthLayout>} />
      <Route
        path="/analytics"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><Analytics /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/settings/tokens"
        element={
          <RequireAuth>
            <Layout>
              <ProtectedRoute requiredRoles={['admin']}>
                <LazyPage><ApiTokens /></LazyPage>
              </ProtectedRoute>
            </Layout>
          </RequireAuth>
        }
      />
      <Route path="/account/sessions" element={<AuthLayout><ActiveSessions /></AuthLayout>} />
      {/* Public club join page */}
      <Route path="/join/:slug" element={<LazyPage><JoinClub /></LazyPage>} />
      {/* Admin routes (outside AuthLayout — uses separate admin token auth) */}
      <Route path="/admin/login" element={<LazyPage><AdminLogin /></LazyPage>} />
      <Route path="/admin/revenue" element={<AdminLayout><LazyPage><RevenueDashboard /></LazyPage></AdminLayout>} />
      <Route path="/admin/commission" element={<AdminLayout><LazyPage><CommissionDashboard /></LazyPage></AdminLayout>} />
      <Route path="/admin/payouts" element={<AdminLayout><LazyPage><PayoutReports /></LazyPage></AdminLayout>} />
      <Route path="/admin/versions" element={<AdminLayout><LazyPage><VersionDashboard /></LazyPage></AdminLayout>} />
      <Route path="/admin/clubs" element={<AdminLayout><LazyPage><PlatformClubs /></LazyPage></AdminLayout>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
